import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    BookOpen, Shield, ArrowRight, CheckCircle2, Clock, Bot,
    Sparkles, Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, Upload, Trash2,
    ExternalLink, CheckSquare, Square, FileText, Bookmark, RotateCcw
} from "lucide-react";

export default function Runbooks() {
    const navigate = useNavigate();
    const [runbooks, setRunbooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTag, setSelectedTag] = useState("ALL");
    const [selectedRunbook, setSelectedRunbook] = useState(null);
    const [toast, setToast] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [checkedSteps, setCheckedSteps] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("soc_runbook_steps") || "{}");
        } catch {
            return {};
        }
    });
    const [newRunbookForm, setNewRunbookForm] = useState({
        name: "",
        description: "",
        tags: "INCIDENT, FORTINET",
        linkedWorkflow: "wf-fortigate-perimeter-containment",
        phase1: "Query FortiAnalyzer (10.180.80.50) for patient-zero host telemetry\nIdentify origin IP and active session count",
        phase2: "Execute FortiGate perimeter drop list injection\nDispatch incident alert to Zoho Desk"
    });

    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchRunbooks();
    }, []);

    const fetchRunbooks = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${backendURL}/api/respond/runbooks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRunbooks(res.data.runbooks || []);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load runbooks:", err);
        }
        setLoading(false);
    };

    const handleCreateRunbook = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("token");
            const payload = {
                name: newRunbookForm.name,
                description: newRunbookForm.description,
                tags: newRunbookForm.tags.split(",").map(t => t.trim().toUpperCase()),
                linkedWorkflow: newRunbookForm.linkedWorkflow || null,
                phases: [
                  {
                    name: "Phase 1: Initial Triage & Investigation",
                    steps: newRunbookForm.phase1.split("\n").filter(s => s.trim())
                  },
                  {
                    name: "Phase 2: Containment & Remediation",
                    steps: newRunbookForm.phase2.split("\n").filter(s => s.trim())
                  }
                ]
            };
            const res = await axios.post(`${backendURL}/api/respond/runbooks`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setRunbooks(prev => [res.data.runbook, ...prev]);
                setCreateModalOpen(false);
                setNewRunbookForm({
                    name: "",
                    description: "",
                    tags: "INCIDENT, FORTINET",
                    linkedWorkflow: "wf-fortigate-perimeter-containment",
                    phase1: "Query FortiAnalyzer (10.180.80.50) for patient-zero host telemetry\nIdentify origin IP and active session count",
                    phase2: "Execute FortiGate perimeter drop list injection\nDispatch incident alert to Zoho Desk"
                });
                setToast(`Runbook "${res.data.runbook.name}" Created & Published!`);
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to create runbook:", err);
            setToast("Failed to create runbook: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handleDeleteRunbook = async (e, rbId) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const rb = runbooks.find(r => r.id === rbId);
        const name = rb?.name || rbId;
        const confirmed = window.confirm(`Are you sure you want to permanently delete the runbook "${name}"?`);
        if (!confirmed) return;

        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${backendURL}/api/respond/runbooks/${rbId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRunbooks(prev => prev.filter(r => r.id !== rbId));
            if (selectedRunbook?.id === rbId) {
                setSelectedRunbook(null);
            }
            setToast(`Runbook "${name}" deleted successfully`);
            setTimeout(() => setToast(null), 3000);
        } catch (err) {
            console.error("Failed to delete runbook:", err);
            setToast("Failed to delete runbook: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                let payload = null;

                if (file.name.endsWith(".json")) {
                    const parsed = JSON.parse(text);
                    payload = {
                        name: parsed.name || file.name.replace(/\.[^/.]+$/, ""),
                        description: parsed.description || "Imported runbook procedure",
                        tags: Array.isArray(parsed.tags) ? parsed.tags : ["IMPORTED", "SOP"],
                        linkedWorkflow: parsed.linkedWorkflow || "wf-fortigate-perimeter-containment",
                        phases: Array.isArray(parsed.phases) ? parsed.phases : [
                            { name: "Phase 1: Triage", steps: ["Execute imported checklist item."] }
                        ]
                    };
                } else {
                    // Markdown or plain text
                    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
                    const title = lines[0]?.replace(/^[#\s]+/, "") || file.name.replace(/\.[^/.]+$/, "");
                    const desc = lines[1] || "Custom imported procedure from document";
                    const steps = lines.slice(2).filter(l => l.length > 2).map(l => l.replace(/^[-*0-9.)\s]+/, ""));

                    payload = {
                        name: title,
                        description: desc,
                        tags: ["IMPORTED", "DOC"],
                        linkedWorkflow: "wf-fortigate-perimeter-containment",
                        phases: [
                            { name: "Phase 1: Standard Procedure", steps: steps.length ? steps : ["Review uploaded document content."] }
                        ]
                    };
                }

                const token = localStorage.getItem("token");
                const res = await axios.post(`${backendURL}/api/respond/runbooks`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data.success) {
                    setRunbooks(prev => [res.data.runbook, ...prev]);
                    setToast(`Imported "${payload.name}" successfully!`);
                    setTimeout(() => setToast(null), 3000);
                }
            } catch (err) {
                console.error("Failed to parse/upload runbook file:", err);
                setToast("Error parsing file. Please provide valid JSON or Markdown.");
                setTimeout(() => setToast(null), 3500);
            }
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    const toggleStep = (stepKey) => {
        setCheckedSteps(prev => {
            const next = { ...prev, [stepKey]: !prev[stepKey] };
            try {
                localStorage.setItem("soc_runbook_steps", JSON.stringify(next));
            } catch (e) {}
            return next;
        });
    };

    const resetStepsForRunbook = (rbId) => {
        setCheckedSteps(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(k => {
                if (k.startsWith(`${rbId}-`)) {
                    delete next[k];
                }
            });
            try {
                localStorage.setItem("soc_runbook_steps", JSON.stringify(next));
            } catch (e) {}
            return next;
        });
        setToast("Checklist progress reset");
        setTimeout(() => setToast(null), 2000);
    };

    const allTags = ["ALL", "RANSOMWARE", "PHISHING", "IDENTITY", "CLOUD", "ENDPOINT", "C2", "NETWORK", "MALWARE"];

    const filteredRunbooks = runbooks.filter(r => {
        const matchesSearch =
            r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTag = selectedTag === "ALL" || r.tags?.includes(selectedTag);
        return matchesSearch && matchesTag;
    });

    const getTagColor = (tag) => {
        switch (tag) {
            case "RANSOMWARE":
            case "MALWARE":
                return "bg-rose-500/10 text-rose-300 border-rose-500/20";
            case "PHISHING":
            case "EMAIL":
                return "bg-blue-500/10 text-blue-300 border-blue-500/20";
            case "IDENTITY":
            case "ACCOUNT COMPROMISE":
                return "bg-purple-500/10 text-purple-300 border-purple-500/20";
            case "CLOUD":
            case "IAM":
                return "bg-amber-500/10 text-amber-300 border-amber-500/20";
            default:
                return "bg-slate-700/30 text-slate-300 border-white/[0.08]";
        }
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>05</span>
                <span>/</span>
                <span className="text-slate-400">Runbooks</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Your response procedures, in the platform.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            The procedures your team relies on, ransomware, phishing, account compromise, cloud anomalies, live alongside the work instead of in a wiki nobody opens. Every case follows a known path, and new analysts inherit your best response on day one.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Codified procedures for the incidents you actually see.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Consistent response, regardless of who is on call.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Institutional knowledge that does not walk out the door.</span>
                        </div>
                    </div>

                    {/* Quick Stats Summary Card */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">Runbooks Status</span>
                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                NIST / SANS Aligned
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Active SOPs</span>
                                <span className="text-[18px] font-bold text-white font-mono">
                                    {runbooks.length} Procedures
                                </span>
                            </div>
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Automated Workflows</span>
                                <span className="text-[18px] font-bold text-blue-400 font-mono">
                                    {runbooks.filter(r => r.linkedWorkflow).length} Linked
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Runbooks Inventory Table (7.5 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-6">
                    
                    {/* Header Controls Bar matching screenshot 05 */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                        <div className="flex items-center gap-2 flex-1">
                            {/* Search */}
                            <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-blue-500/40">
                                <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Search runbooks..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                                />
                            </div>

                            {/* Tag Filter */}
                            <select
                                value={selectedTag}
                                onChange={(e) => setSelectedTag(e.target.value)}
                                className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-2.5 py-1.5 outline-none"
                            >
                                {allTags.map((t, idx) => (
                                    <option key={idx} value={t}>{t === "ALL" ? "Filter by tags" : t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".json,.md,.txt"
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11.5px] font-medium flex items-center gap-1.5 border border-white/[0.06] transition-all"
                                title="Import runbook from JSON or Markdown document"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                Upload Runbook
                            </button>
                            <button
                                onClick={() => setCreateModalOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                New Runbook
                            </button>
                        </div>
                    </div>

                    {/* Runbooks Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-[12px]">
                            <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                <tr>
                                    <th className="py-3 px-4">Name</th>
                                    <th className="py-3 px-4">Description</th>
                                    <th className="py-3 px-3">Tags</th>
                                    <th className="py-3 px-3">Created</th>
                                    <th className="py-3 px-3">Last modified</th>
                                    <th className="py-3 px-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {filteredRunbooks.map((rb) => {
                                    const firstTwoTags = rb.tags?.slice(0, 2) || [];
                                    const extraTagsCount = (rb.tags?.length || 0) - 2;

                                    return (
                                        <tr
                                            key={rb.id}
                                            onClick={() => setSelectedRunbook(rb)}
                                            className="hover:bg-white/[0.02] cursor-pointer transition-colors group"
                                        >
                                            {/* Name */}
                                            <td className="py-3.5 px-4 font-bold text-white group-hover:text-blue-400 transition-colors whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <BookOpen className="w-3.5 h-3.5 text-blue-400/70" />
                                                    <span>{rb.name}</span>
                                                </div>
                                            </td>

                                            {/* Description */}
                                            <td className="py-3.5 px-4 text-slate-400 text-[11.5px] max-w-xs truncate" title={rb.description}>
                                                {rb.description}
                                            </td>

                                            {/* Tags */}
                                            <td className="py-3.5 px-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    {firstTwoTags.map((tag, tIdx) => (
                                                        <span
                                                            key={tIdx}
                                                            className={`text-[8.5px] font-mono uppercase px-1.5 py-0.5 rounded font-semibold border ${getTagColor(tag)}`}
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                    {extraTagsCount > 0 && (
                                                        <span className="text-[8.5px] font-mono text-slate-400 bg-white/[0.04] px-1 py-0.5 rounded">
                                                            +{extraTagsCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Created */}
                                            <td className="py-3.5 px-3 font-mono text-slate-500 text-[11px] whitespace-nowrap">
                                                {rb.created}
                                            </td>

                                            {/* Last Modified */}
                                            <td className="py-3.5 px-3 font-mono text-slate-300 text-[11px] whitespace-nowrap">
                                                {rb.lastModified}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3.5 px-2 text-right whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleDeleteRunbook(e, rb.id)}
                                                    className="p-1 hover:bg-rose-600 text-slate-500 hover:text-white rounded opacity-0 group-hover:opacity-100 transition-all"
                                                    title="Delete runbook"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ─── Runbook SOP Inspector Slide-Over / Modal ─── */}
            {selectedRunbook && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-2xl p-6 space-y-5 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px] max-h-[85vh] overflow-y-auto custom-scrollbar">
                        
                        {/* Drawer Header */}
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="space-y-0.5">
                                <span className="text-[10px] font-mono uppercase text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                                    Standard Operating Procedure
                                </span>
                                <h3 className="text-[17px] font-black text-white tracking-tight">
                                    {selectedRunbook.name}
                                </h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => resetStepsForRunbook(selectedRunbook.id)}
                                    className="px-2.5 py-1 text-slate-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded-lg text-[11px] flex items-center gap-1"
                                    title="Reset checklist items"
                                >
                                    <RotateCcw className="w-3 h-3" />
                                    Reset
                                </button>
                                <button onClick={() => setSelectedRunbook(null)} className="text-slate-400 hover:text-white">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Description & Tags */}
                        <p className="text-[12.5px] text-slate-300 leading-relaxed">
                            {selectedRunbook.description}
                        </p>

                        <div className="flex flex-wrap gap-1.5">
                            {selectedRunbook.tags?.map((tag, tIdx) => (
                                <span key={tIdx} className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold border ${getTagColor(tag)}`}>
                                    {tag}
                                </span>
                            ))}
                        </div>

                        {/* Phased Checklist */}
                        <div className="space-y-4 pt-2 border-t border-white/[0.06]">
                            {(selectedRunbook.phases || []).map((phase, pIdx) => (
                                <div key={pIdx} className="bg-black/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
                                    <h4 className="text-[13px] font-bold text-white flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center text-[10px] font-bold font-mono">
                                            {pIdx + 1}
                                        </div>
                                        {phase.name}
                                    </h4>

                                    <div className="space-y-2 pl-7">
                                        {(phase.steps || []).map((step, sIdx) => {
                                            const stepKey = `${selectedRunbook.id}-${pIdx}-${sIdx}`;
                                            const isDone = checkedSteps[stepKey];

                                            return (
                                                <div
                                                    key={sIdx}
                                                    onClick={() => toggleStep(stepKey)}
                                                    className="flex items-start gap-2.5 cursor-pointer select-none group"
                                                >
                                                    <div className="pt-0.5">
                                                        {isDone ? (
                                                            <CheckSquare className="w-4 h-4 text-emerald-400" />
                                                        ) : (
                                                            <Square className="w-4 h-4 text-slate-500 group-hover:text-slate-300" />
                                                        )}
                                                    </div>
                                                    <span className={`text-[12px] leading-relaxed transition-colors ${
                                                        isDone ? "line-through text-slate-500" : "text-slate-200 group-hover:text-white"
                                                    }`}>
                                                        {step}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer & Linked Workflow Button */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-white/[0.06]">
                            <div className="flex items-center gap-2">
                                {selectedRunbook.linkedWorkflow ? (
                                    <Link
                                        to={`/workflows?id=${selectedRunbook.linkedWorkflow}`}
                                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-bold text-[12px] shadow-lg shadow-purple-600/20 flex items-center gap-1.5 justify-center"
                                    >
                                        <Zap className="w-3.5 h-3.5" />
                                        Launch Automated Workflow →
                                    </Link>
                                ) : (
                                    <span className="text-[11px] font-mono text-slate-500">
                                        Manual response procedure
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={(e) => handleDeleteRunbook(e, selectedRunbook.id)}
                                    className="px-3 py-2 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-xl font-bold text-[11.5px] border border-rose-500/30 transition-all flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                </button>
                            </div>

                            <button
                                onClick={() => setSelectedRunbook(null)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-xl text-[12px]"
                            >
                                Close
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* ─── New Runbook Modal ─── */}
            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Create Response Runbook</h3>
                            <button onClick={() => setCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateRunbook} className="space-y-3.5">
                            <div>
                                <label className="text-slate-400 block mb-1">Runbook Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. FortiGate C2 Autonomous Containment"
                                    value={newRunbookForm.name}
                                    onChange={(e) => setNewRunbookForm({ ...newRunbookForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Description</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Standard operating procedure for perimeter firewall quarantine"
                                    value={newRunbookForm.description}
                                    onChange={(e) => setNewRunbookForm({ ...newRunbookForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Tags (Comma separated)</label>
                                    <input
                                        type="text"
                                        placeholder="C2, MALWARE, NETWORK"
                                        value={newRunbookForm.tags}
                                        onChange={(e) => setNewRunbookForm({ ...newRunbookForm, tags: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none uppercase font-mono text-[11px]"
                                    />
                                </div>
                                <div>
                                    <label className="text-slate-400 block mb-1">Link Automated Workflow</label>
                                    <select
                                        value={newRunbookForm.linkedWorkflow}
                                        onChange={(e) => setNewRunbookForm({ ...newRunbookForm, linkedWorkflow: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-2.5 py-2 text-white outline-none text-[11px]"
                                    >
                                        <option value="wf-fortigate-perimeter-containment">FortiGate C2 Containment</option>
                                        <option value="wf-fortios-host-quarantine">FortiOS Host Isolation</option>
                                        <option value="wf-web-dns-filter-defense">Malicious DNS Filter Defense</option>
                                        <option value="wf-create-case-on-investigation">Create Case On Investigation</option>
                                        <option value="">None (Manual SOP)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Phase 1: Triage Steps (One per line)</label>
                                <textarea
                                    rows="2"
                                    value={newRunbookForm.phase1}
                                    onChange={(e) => setNewRunbookForm({ ...newRunbookForm, phase1: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px]"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Phase 2: Containment Steps (One per line)</label>
                                <textarea
                                    rows="2"
                                    value={newRunbookForm.phase2}
                                    onChange={(e) => setNewRunbookForm({ ...newRunbookForm, phase2: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px]"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setCreateModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Save Runbook
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
